/**
 * Scroll utilities for the public landing page.
 *
 * The public page can stack up to two sticky bars at the top of the viewport: the live schedule
 * banner (when shown) and the navbar below it (when the page has a visible Navbar). Any smooth
 * anchor-scroll to a `#section` must subtract the combined height of whichever of those are present,
 * otherwise the target lands behind them. This is the single source of truth for that offset so
 * every anchor handler (navbar, smart links, rich-text links, the banner CTA, on-load hash scroll)
 * stays in sync instead of hardcoding values that drift from the responsive bars — or, worse,
 * over-offsetting on a page that has no banner and/or no navbar.
 */

/**
 * Total pixel offset a smooth anchor-scroll should subtract so the target section isn't hidden
 * behind the sticky chrome (navbar + live schedule banner) at the top of the public page.
 *
 * Each bar publishes its measured height as a CSS variable while it's mounted — the navbar as
 * `--lp-navbar-height` and the banner as `--lp-live-banner-height` — and removes it when it isn't
 * rendered. Reading them here means a page with no banner, no navbar, or neither contributes only
 * the bars that are actually on screen (an absent bar reads as 0), so the scroll always lands right.
 *
 * @param gap - extra breathing room to leave below the chrome, in pixels (defaults to 0)
 * @returns the offset in pixels (navbar height + banner height + gap)
 */
export function getStickyHeaderOffset(gap = 0): number {
  if (typeof document === "undefined") return gap;
  const style = getComputedStyle(document.documentElement);
  const navbarHeight = Number.parseFloat(style.getPropertyValue("--lp-navbar-height")) || 0;
  const bannerHeight = Number.parseFloat(style.getPropertyValue("--lp-live-banner-height")) || 0;
  return navbarHeight + bannerHeight + gap;
}

/**
 * Smooth-scroll to the in-page `#section` with id `sectionId`, clearing the sticky chrome above it
 * ({@link getStickyHeaderOffset}), then sync the URL hash without a navigation.
 *
 * Single source of truth for the navbar, dropdown, and overflow-menu anchor handlers so the scroll
 * math and history update don't drift across copies. Callers are responsible for `preventDefault()`,
 * closing any open menu, and only invoking this for same-page anchors (not cross-page links).
 *
 * @param sectionId - the target element id (no leading `#`)
 * @param gap - extra breathing room below the chrome, in pixels (defaults to 0)
 * @returns nothing
 */
export function scrollToAnchor(sectionId: string, gap = 0): void {
  const el = document.getElementById(sectionId);
  if (el) {
    const top = el.getBoundingClientRect().top + window.scrollY - getStickyHeaderOffset(gap);
    window.scrollTo({ top, behavior: "smooth" });
  }
  window.history.pushState(null, "", `#${sectionId}`);
}
